import { TradeClient } from '../grpc/generated/trade_grpc_web_pb';
import { serverApiConfig } from '../config';
import { requestGoogleProtobuf } from '../utilities/request-google-protobuf';
import { metadata as getMetadata } from './metadata';
import { getGrpcResponse } from '../utilities/getGrpcResponse';
import { appendOrders, deleteOrders } from '../redux/reducer/orders';
import { appendPosition, deletePosition, updatePosition } from '../redux/reducer/positions';
import { setTradeEvent, setTradeEventStatus } from '../redux/reducer/event';

const EVENT_HANDLERS = {
  ORDER_ADDED: (data, dispatch) => {
    dispatch(appendOrders(data[2]));
  },
  ORDER_DELETED: (data, dispatch) => {
    dispatch(deleteOrders(data[2]));
  },
  POSITION_ADDED: (data, dispatch) => {
    dispatch(appendPosition(data[1]));
  },
  POSITION_UPDATED: (data, dispatch) => {
    dispatch(updatePosition(data[1]));
  },
  POSITION_DELETED: (data, dispatch) => {
    dispatch(deletePosition(data[1]));
  },
  BALANCE_UPDATED: (data, dispatch, event) => {
    dispatch(setTradeEvent(event));
  },
  CREDIT_UPDATED: (data, dispatch, event) => {
    dispatch(setTradeEvent(event));
  }
};

class TradeEventService {
  tradeClient;
  call;
  errorCount = null;
  isErrorConnect = false;
  activeSymbol = '';
  dispatch;
  reconnectParams = null;
  onlineListener = null;
  static RETRY_LIMIT = 3;
  static RETRY_DELAY = 5000;

  constructor(dispatch) {
    this.dispatch = dispatch;
    this.tradeClient = this.createTradeClient();
    this.setupConnectivityListeners();
  }

  setupConnectivityListeners() {
    // Listen for online event
    this.onlineListener = () => {
      console.log('Internet connection restored - TradeEvent');
      if (this.isErrorConnect && this.reconnectParams) {
        console.log('Attempting to reconnect TradeEvent...');
        this.attemptReconnect();
      }
    };

    // Listen for visibility change (tab/window hidden/visible)
    this.visibilityListener = () => {
      console.log('TradeEvent: Visibility changed to:', document.visibilityState);
      if (document.visibilityState === 'visible') {
        console.log('TradeEvent: Page became visible, checking connection...');
        // Wait a bit for network to stabilize after wake
        setTimeout(() => {
          this.checkAndReconnect();
        }, 1500); // Slightly longer delay than onTick
      } else {
        console.log('TradeEvent: Page became hidden');
      }
    };

    // Listen for page resume (wake from sleep)
    this.resumeListener = () => {
      console.log('System resumed from sleep, checking TradeEvent connection...');
      setTimeout(() => {
        this.checkAndReconnect();
      }, 1500);
    };

    window.addEventListener('online', this.onlineListener);
    document.addEventListener('visibilitychange', this.visibilityListener);
    window.addEventListener('resume', this.resumeListener);

    // Also listen for focus event as backup
    this.focusListener = () => {
      if (this.lastActivityTime && Date.now() - this.lastActivityTime > 60000) {
        console.log('Window focus after long inactivity, checking TradeEvent connection...');
        this.checkAndReconnect();
      }
      this.lastActivityTime = Date.now();
    };
    window.addEventListener('focus', this.focusListener);

    // Track last activity
    this.lastActivityTime = Date.now();
  }

  checkAndReconnect() {
    console.log('TradeEvent checkAndReconnect called', {
      hasCall: !!this.call,
      isErrorConnect: this.isErrorConnect,
      hasReconnectParams: !!this.reconnectParams
    });

    // If we don't have reconnect params yet, can't reconnect
    if (!this.reconnectParams) {
      console.log('TradeEvent: No reconnect params available, skipping reconnect');
      return;
    }

    // If we have reconnect params and are in error state, reconnect
    if (this.isErrorConnect) {
      console.log('TradeEvent: Forcing reconnect after wake/visibility change (error state)...');
      this.attemptReconnect();
      return;
    }

    // Even if not in error state, connection might be stale after sleep/wake
    // Force reconnect proactively
    if (this.call) {
      console.log('TradeEvent: Proactive reconnect - cancelling existing connection...');
      try {
        this.call.cancel();
      } catch (e) {
        console.log('TradeEvent: Error cancelling call:', e);
      }
      this.isErrorConnect = true;
      setTimeout(() => {
        console.log('TradeEvent: Attempting reconnect after cancel...');
        this.attemptReconnect();
      }, 500);
    } else {
      console.log('TradeEvent: No active call, initiating fresh connection...');
      this.isErrorConnect = true;
      this.attemptReconnect();
    }
  }

  cleanup() {
    if (this.onlineListener) {
      window.removeEventListener('online', this.onlineListener);
    }
    if (this.visibilityListener) {
      document.removeEventListener('visibilitychange', this.visibilityListener);
    }
    if (this.resumeListener) {
      window.removeEventListener('resume', this.resumeListener);
    }
    if (this.focusListener) {
      window.removeEventListener('focus', this.focusListener);
    }
    if (this.call) {
      this.call.cancel();
    }
  }

  isOnline() {
    return navigator.onLine;
  }

  createTradeClient() {
    const server = localStorage.getItem('trade.server');
    return new TradeClient(serverApiConfig(server), null, null);
  }

  async handleError() {
    this.errorCount++;
    this.isErrorConnect = true;

    if (this.errorCount > TradeEventService.RETRY_LIMIT) {
      console.error('TradeEvent: Max retry attempts reached');
      this.reconnectParams = null;
      return;
    }

    // Store reconnection flag
    this.reconnectParams = { shouldReconnect: true };

    try {
      this.call?.cancel();

      // Check if online before attempting reconnection
      if (this.isOnline()) {
        console.log('Internet is online, attempting immediate TradeEvent reconnect');
        setTimeout(() => {
          this.attemptReconnect();
        }, TradeEventService.RETRY_DELAY);
      } else {
        console.log('Internet is offline, waiting for connection to be restored...');
        // Will reconnect automatically when 'online' event fires
      }
    } catch (error) {
      console.error('Error during TradeEvent reconnection:', error);
    }
  }

  attemptReconnect() {
    if (!this.reconnectParams) {
      console.log('No reconnection parameters available for TradeEvent');
      return;
    }

    console.log('Reconnecting to TradeEvent');
    this.onSubscribe();
  }

  setupCallListeners() {
    this.call.on('data', (response) => {
      this.isErrorConnect = false;
      this.errorCount = 0; // Reset error count on successful connection
      this.reconnectParams = null; // Clear reconnect params
      const data = getGrpcResponse(response);
      const eventType = data?.[0];
      if (eventType && eventType in EVENT_HANDLERS) {
        EVENT_HANDLERS[eventType](data, this.dispatch, eventType);
      }
    });

    this.call.on('status', (status) => {
      console.log('TradeEvent status:', status);
      // Handle status updates if needed
    });

    this.call.on('error', (err) => {
      console.log('TradeEvent connection error:', err);
      this.handleError(err);
    });
  }

  async onSubscribe() {
    // Always store params for proactive reconnect (update every time)
    this.reconnectParams = { shouldReconnect: true };

    console.log('TradeEvent onSubscribe called', {
      hasCall: !!this.call,
      isErrorConnect: this.isErrorConnect
    });

    try {
      if (!this.call) {
        console.log('TradeEvent: Creating initial connection...');
        this.call = await this.tradeClient.subscribe(requestGoogleProtobuf, getMetadata());
        this.setupCallListeners();
        console.log('TradeEvent: Initial connection established successfully');
        this.dispatch(setTradeEventStatus(true));
        return;
      }

      if (this.isErrorConnect) {
        // Refresh metadata to get new token
        const freshMetadata = getMetadata();
        console.log('TradeEvent: Reconnecting after error with fresh metadata...');
        this.call = await this.tradeClient.subscribe(requestGoogleProtobuf, freshMetadata);
        this.isErrorConnect = false;
        this.setupCallListeners();
        this.dispatch(setTradeEventStatus(true));
        console.log('TradeEvent: Reconnected and listeners set up successfully');
        return;
      }
    } catch (error) {
      console.error('TradeEvent: Error in onSubscribe:', error);
      this.dispatch(setTradeEventStatus(false));
      throw error;
    }
  }
}

const TradeEvent = (dispatch) => {
  const tradeEventService = new TradeEventService(dispatch);
  return {
    onEvent: tradeEventService.onSubscribe.bind(tradeEventService),
    cleanup: tradeEventService.cleanup.bind(tradeEventService)
  };
};

export default TradeEvent;
